// FP 자원 간트 — 하단 상세탭 ② 생산 차질
//
// old: `analysis/resourcegantt/tabs/ShortageDetailTab.jsx`
// API: `POST factoryplan/analysis/resource-gantt/short-activities`(이관 완료,
//      `AnalysisController#getShortageActivities`). 응답 `ShortageActivityDto` 23필드가 old 그리드
//      컬럼과 완전히 일치한다.
//
// **바 선택과 무관하다.** old도 `loadData`에서 간트 본조회와 같은 params로 1회 받아
// `shortageData` state에 넣고 탭에 그대로 넘겼다(활동 단위가 아니라 조회 범위 전체의 차질 목록).
//
// `groupShowMode` 매핑은 ResourcePlanDetailTab과 같다(always → 생략, expand → columnGroupShow:"open").
//
// **타입별 정렬은 다른 Report 화면과 동일하게 맞춘다**(2026-08-24 사용자 지시):
//   숫자 → `["numberColumn", "rightAligned"]` / 일시·날짜 → `["centerAligned"]` / 불리언 →
//   `["booleanColumn", "centerAligned", "nonEditableColumn"]` / 문자 → 지정 없음(좌측 기본).
//
// old `setGridComboList(gridView, 'itemClassCd', 'FP_ITEM_CLASS_CD')`는 **이관하지 않는다** —
// 이 그리드 컬럼 정의에 `itemClassCd`가 아예 없어서 old에서도 아무 일도 하지 않던 죽은 코드다.
//
// TODO(MIG-T2): [resourcegantt/ResourceGantt] 생산 차질 — 동일값 셀 병합(mergeRule) 미이관.
//   old: 주문 그룹 전체 + 차질/문제 컬럼에 mergeRule(criteria "values['dmndCd'] + values['woCd'] + value" 등)
//   사유: Tier 2(셀 병합) 유보 항목. 정렬로 같은 주문 행이 인접해 값이 반복 렌더되므로 판독은 가능하다

import { useMemo, useRef } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import { red } from "@mui/material/colors";

import { format } from "date-fns";

import FpGridSetting from "@plannel/pages/factory-plan/common/grid";

const DATE_FORMAT = "yyyy-MM-dd";

const formatDate = (params) => {
  if (!params.value) return "";
  const date = new Date(params.value);
  return Number.isNaN(date.getTime()) ? String(params.value) : format(date, DATE_FORMAT);
};

// 지연 수량·지연 시간이 있는 행을 옅게 강조한다 — old에는 없던 표시지만 이 탭 자체가 "차질 목록"이라
// 지연/결품 컬럼이 판독의 핵심이다. 배경 틴트만 쓴다(다른 Report 화면의 강조 관례와 동일).
const shortageCellStyle = (params) => (params.value ? { backgroundColor: red[50], color: red[800] } : null);

const ShortageDetailTab = ({ rows, viewName, t }) => {
  const gridRef = useRef(null);

  const columnDefs = useMemo(() => [
    {
      headerName: t("FP_ORDER"), openByDefault: false, children: [
        { headerName: t("FP_SALES_ORDER"), field: "dmndCd", width: 200, columnGroupShow: "open" },
        { headerName: t("FP_CUSTOMER_CD"), field: "customerCd", width: 140, columnGroupShow: "open" },
        { headerName: t("FP_CUSTOMER_NM"), field: "customerNm", width: 140, columnGroupShow: "open" },
        { headerName: t("FP_CUSTOMER_PRIORITY"), field: "customerPriority", width: 100, type: ["numberColumn", "rightAligned"], columnGroupShow: "open" },
        { headerName: t("FP_SALES_ORDER_QTY"), field: "dmndQty", width: 130, type: ["numberColumn", "rightAligned"], columnGroupShow: "open" },
        { headerName: t("FP_WO_CD"), field: "woCd", width: 240 },
        { headerName: t("FP_PRIORITY"), field: "woPriority", width: 90, type: ["numberColumn", "rightAligned"], columnGroupShow: "open" },
        { headerName: t("FP_WO_ITEM_CD"), field: "woItemCd", width: 140 },
        { headerName: t("FP_WO_ITEM_NM"), field: "woItemNm", width: 140, columnGroupShow: "open" },
        { headerName: t("FP_DUE_DT"), field: "dueDate", width: 130, type: ["centerAligned"], valueFormatter: formatDate },
        // 폭 150 — 헤더 "작업 주문 수량"이 우측 정렬 메뉴 아이콘과 겹쳐 잘린다(ResourcePlanDetailTab과 동일).
        { headerName: t("FP_ORDER_QTY"), field: "orderQty", width: 150, type: ["numberColumn", "rightAligned"] }
      ]
    },
    {
      // old는 이 그룹만 expandable:false — 4개 전부 always라 토글이 필요 없다.
      // lateYn/shtgYn은 old dataType:"boolean" → 읽기전용 체크박스 렌더(§0-4 `booleanColumn`).
      headerName: t("PROD_PRBM"), children: [
        // 폭 110 — 헤더("지연 여부"/"결품 여부")가 정렬 메뉴 아이콘과 겹쳐 잘렸다(2026-08-24 사용자 지시).
        { headerName: t("FP_LATE_YN"), field: "lateYn", width: 110, type: ["booleanColumn", "centerAligned", "nonEditableColumn"] },
        { headerName: t("FP_SHTG_YN"), field: "shtgYn", width: 110, type: ["booleanColumn", "centerAligned", "nonEditableColumn"] },
        // `rightAligned`는 AG Grid 내장 타입이고 `cellClass`/`headerClass`로 동작하므로, 아래
        // `cellStyle`(지연 강조)과 **충돌하지 않는다**. `centerAligned`(FP 자체 타입)는 `cellStyle`을
        // 쓰기 때문에 colDef의 cellStyle이 그걸 덮어쓴다 — 그래서 두 계열을 섞어 쓰지 않는다
        // (같은 함정을 ResourcePlanDetailTab의 `CENTER_CELL` 주석에 기록해 뒀다).
        { headerName: t("LATE_QTY"), field: "lateQty", width: 110, type: ["numberColumn", "rightAligned"], cellStyle: shortageCellStyle },
        { headerName: t("FP_LATE_TIME"), field: "latenessTime", width: 130, type: ["numberColumn", "rightAligned"], cellStyle: shortageCellStyle }
      ]
    },
    {
      headerName: t("PRBM_STATUS"), openByDefault: false, children: [
        // old displayCallback: (grid, index, value) => transLangKey(value) — 값 자체가 다국어 키다.
        { headerName: t("FP_PROBLEM_TP_CD"), field: "problemTpCd", width: 130, valueFormatter: (params) => (params.value ? t(params.value) : "") },
        { headerName: t("FP_PROBLEM_REASON_TP_CD"), field: "problemReasonTpCd", width: 130, valueFormatter: (params) => (params.value ? t(params.value) : "") },
        { headerName: t("FP_PLAN_PROBLEM_ITEM_CD"), field: "problemItemCd", width: 130, columnGroupShow: "open" },
        { headerName: t("FP_PLAN_PROBLEM_ITEM_NM"), field: "problemItemNm", width: 130, columnGroupShow: "open" },
        { headerName: t("FP_PLAN_PROBLEM_ROUTE_CD"), field: "problemRouteCd", width: 130, columnGroupShow: "open" },
        { headerName: t("FP_PLAN_PROBLEM_ROUTE_NM"), field: "problemRouteNm", width: 130, columnGroupShow: "open" },
        { headerName: t("FP_PLAN_PROBLEM_RESOURCE_CD"), field: "problemResourceCd", width: 130, columnGroupShow: "open" },
        { headerName: t("FP_PLAN_PROBLEM_RESOURCE_NM"), field: "problemResourceNm", width: 130, columnGroupShow: "open" }
      ]
    }
  ], [t]);

  const gridOptions = useMemo(() => {
    const base = FpGridSetting({ title: "PROD_PRBM", viewName });
    return { ...base, defaultColDef: { ...base.defaultColDef, editable: false, sortable: true, resizable: true } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 하단 상태바(Total Rows)는 두지 않는다 — 하단 탭 3개 공통 규칙이다(2026-08-24 사용자 지시, §7-25).
  //
  // ⚠️ **`getRowId`를 두지 않는다**(2026-08-24, §7-24).
  //
  // 예전에는 `dmndCd|woCd|problemTpCd|problemItemCd|problemResourceCd` 조합 키를 만들었는데
  // **그 키가 중복될 수 있다.** 백엔드가 `woPlan LEFT JOIN planProblem`을 (planScope, versionCd,
  // stageCd, woCd)로만 걸어서(`ResourceGanttQueryRepository.getShortageActivities`) **한 작업 주문에
  // 문제 행이 여러 개** 달리는데, 키에 `problemRouteCd`/`problemReasonTpCd`가 빠져 있어 경로나 사유만
  // 다른 문제들이 **같은 id**가 됐다.
  //
  // AG Grid는 row id가 유일하다고 전제한다 — 중복이면 **선택 상태를 같은 id끼리 공유**해서
  // 셀을 하나 클릭했는데 여러 행이 함께 선택되고, 다른 셀을 클릭해도 이전 선택이 안 풀린다
  // (사용자 보고 증상 그대로다. 스크린샷에서 전 행의 주문·문제 유형·문제 상세가 모두 같다).
  //
  // 키를 더 촘촘히 만드는 대신 **아예 빼서** AG Grid가 자기 내부 id를 쓰게 한다 — 이 그리드는
  // 읽기 전용이라 `applyTransaction`·선택 유지·`refreshCells`를 쓰는 코드가 하나도 없다(확인함).
  // 나머지 두 탭(`ResourcePlanDetailTab`·`PsiTab`)도 같은 이유로 `getRowId`가 없다.
  return (
    <Box className="ag-theme-balham grid-area" sx={{ height: "100%" }}>
      <AgGridReact
        ref={gridRef}
        rowData={rows}
        columnDefs={columnDefs}
        {...gridOptions}
      />
    </Box>
  );
};

export default ShortageDetailTab;
